import React, { useRef, useEffect } from 'react';

const experiences = [
  {
    title: "Secondary Education (CBSE)",
    company: "St. Joseph's High School, Patna",
    date: "2020",
    description: "Completed secondary education with a strong foundation in science and mathematics."
  },
  {
    title: "Higher Secondary Education (CBSE)",
    company: "St. Joseph's High School, Patna",
    date: "2022",
    description: "Focused on Physics, Chemistry, and Mathematics with Computer Science as an elective."
  },
  {
    title: "B.Tech in Computer Science with AI/ML Minor",
    company: "Tula's Institute, Dehradun",
    date: "2022 – 2026",
    description: "Pursuing major in Computer Science and minor in Artificial Intelligence & Machine Learning."
  },
  {
    title: "Secretary - IEEE Student Branch",
    company: "Tula's Institute",
    date: "2024 – 2025",
    description: "Organized 15+ technical events, including international conferences and workshops. Received Community Service Award."
  },
  {
    title: "Web Development Certification",
    company: "Udemy",
    date: "2024",
    description: "Completed a project-based web development course."
  },
  {
    title: "Finalist - Janhit Jagran & Runner-Up - Technovate for India",
    company: "Dainik Jagran, Times of India & Talrop",
    date: "2024–2025",
    description: "Recognized for innovation and creativity in national-level tech competitions."
  },
  {
    title: "Machine Learning Course (Ongoing)",
    company: "Udemy",
    date: "2025 – Present",
    description: "Currently learning ML concepts including supervised/unsupervised algorithms and real-world implementation."
  }
];

const Experience = () => {
  const itemsRef = useRef([]);

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const element = entry.target;
          const dot = element.querySelector('.timeline-dot');
          const line = element.querySelector('.timeline-line');

          if (entry.isIntersecting) {
            element.classList.add('animate-fade-in-up');
            if (dot) dot.classList.add('animate-pulse-once');
            if (line) line.classList.add('animate-draw-line');
          } else {
            // Remove classes when out of view
            element.classList.remove('animate-fade-in-up');
            if (dot) dot.classList.remove('animate-pulse-once');
            if (line && !entry.isIntersecting) line.classList.remove('animate-draw-line');
          }
        });
      },
      { threshold: 0.1 }
    );

    itemsRef.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      itemsRef.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  return (
    <section id="experience" className="relative py-28 px-6 overflow-hidden">
      
      {/* Floating Tech Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-1/4 left-[10%] w-32 h-32 border-4 border-blue-500/10 rounded-full animate-float-1"></div>
        <div className="absolute bottom-1/3 right-[15%] w-24 h-24 border-3 border-cyan-500/10 rounded-full animate-float-2"></div>
        <div className="absolute top-1/3 right-[25%] w-16 h-16 border-t-4 border-r-4 border-blue-500/10 transform rotate-45 animate-spin-slow"></div>
        <div className="absolute bottom-1/4 left-[25%] w-20 h-20 border-2 border-purple-500/10 rounded-lg transform rotate-12 animate-wave"></div>
      </div>

      {/* Section Content */}
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
            My Journey
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-10">
            A timeline of my academic achievements, certifications, and leadership roles.
          </p>
          <div className="w-40 h-1 mx-auto bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
        </div>

        {/* Enhanced Timeline */}
        <div className="relative">
          {/* Glowing Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-200 via-cyan-200 to-blue-200 opacity-70 shadow-xl shadow-blue-500/30"></div>

          {/* Experience Items */}
          {experiences.map((exp, index) => (
            <div
              key={index}
              ref={(el) => (itemsRef.current[index] = el)}
              className={`mb-24 last:mb-0 relative opacity-0 translate-y-10 ${
                index % 2 === 0 ? 'md:pr-8 md:ml-auto' : 'md:pl-8 md:mr-auto'
              } w-full md:w-5/12`}
            >
              {/* Animated Dot */}
              <div className="timeline-dot absolute left-1/2 transform -translate-x-1/2 -top-4 w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 border-4 border-gray-900 z-10 flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:shadow-blue-500/50 transition-all duration-300">
                <div className="w-3 h-3 rounded-full bg-white animate-pulse"></div>
              </div>

              {/* Dynamic Line Between Nodes */}
              {index > 0 && (
                <div className="timeline-line absolute left-1/2 transform -translate-x-1/2 w-16 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 opacity-70 origin-left scale-x-0 transition-transform duration-700 ease-out"></div>
              )}

              {/* Card Container */}
              <div className="group relative px-8 py-8 bg-gray-900/70 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-xl hover:shadow-blue-500/20 hover:border-blue-500/40 transition-all duration-300">
                {/* Corner Decorations */}
                <div className="absolute top-0 left-0 w-16 h-16 overflow-hidden opacity-30 group-hover:opacity-50 transition-opacity">
                  <div className="absolute top-0 left-0 w-24 h-24 bg-blue-500/10 transform rotate-45 origin-top-left translate-x-[-50%] translate-y-[-50%]"></div>
                </div>
                <div className="absolute bottom-0 right-0 w-16 h-16 overflow-hidden opacity-30 group-hover:opacity-50 transition-opacity">
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-cyan-500/10 transform rotate-45 origin-bottom-right translate-x-[50%] translate-y-[50%]"></div>
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  {exp.title}
                </h3>

                {/* Company */}
                <div className="flex items-center gap-3 mb-3">
                  <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  <p className="text-blue-400 text-lg">{exp.company}</p>
                </div>

                {/* Date */}
                <div className="flex items-center gap-3 mb-6">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <p className="text-gray-400">{exp.date}</p>
                </div>

                {/* Description */}
                <p className="text-gray-300 leading-relaxed relative z-10">
                  {exp.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse-once {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
          }
          70% {
            transform: scale(1.05);
            box-shadow: 0 0 20px 8px rgba(59, 130, 246, 0.3);
          }
        }

        @keyframes float-1 {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }

        @keyframes float-2 {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(15px) scale(1.05); }
        }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes wave {
          0%, 100% { transform: rotate(45deg) scaleX(1); }
          50% { transform: rotate(45deg) scaleX(1.5); }
        }

        @keyframes draw-line {
          from {
            transform: scaleX(0);
          }
          to {
            transform: scaleX(1);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }

        .animate-pulse-once {
          animation: pulse-once 1.5s ease-out forwards;
        }

        .animate-float-1 {
          animation: float-1 8s ease-in-out infinite;
        }

        .animate-float-2 {
          animation: float-2 7s ease-in-out infinite;
        }

        .animate-spin-slow {
          animation: spin-slow 25s linear infinite;
        }

        .animate-wave {
          animation: wave 5s ease-in-out infinite;
        }

        .animate-draw-line {
          animation: draw-line 1s ease-in-out forwards;
        }
      `}</style>
    </section>
  );
};

export default Experience;